import { useEffect, useState, useMemo } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { Input } from "@project/components/ui/input";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@project/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@project/components/ui/radio-group";
import { Label } from "@project/components/ui/label";
import { TablePagination } from "@project/admin/components/table-pagination";
import {
  Search,
  Boxes,
  ChevronLeft,
  Pencil,
  Loader2,
  Package,
  Eye,
  PencilIcon,
} from "lucide-react";
import { gruhUdhyogProductService } from "@project/services/admin/gruh-udhyog-product.service";
import { gruhUdhyogVariantService } from "@project/services/admin/gruh-udhyog-variant.service";
import type { Product, ProductVariant } from "@project/types/gruh-udhyog";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { resolveMediaUrl } from "@project/lib/utils";

// Model for flattened product variant rows shown in the table
interface InventoryRow {
  variantId: number;
  productId: number;
  productName: string;
  categoryName: string;
  image: string | null;
  value: string;
  sku: string;
  price: string;
  totalStock: number;
  reservedStock: number;
  availableStock: number;
  isActive: boolean;
  inStock: boolean;
  vendor: string;
  associatedVariants: ProductVariant[];
  productDetails: Product;
}

export default function InventoryPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Pagination states for the list view
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Selection states
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null
  );

  // Stock Adjustment Modal states
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [stockAction, setStockAction] = useState<"ADD" | "REMOVE">("ADD");
  const [stockQuantity, setStockQuantity] = useState<string>("0");
  const [submittingStock, setSubmittingStock] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await gruhUdhyogProductService.listPage({
        page: 1,
        page_size: 200,
      });
      setProducts(res.data ?? []);
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Failed to load inventory"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchProducts();
  }, []);

  // Transform products list into flat variant rows
  const inventoryRows: InventoryRow[] = useMemo(() => {
    const rows: InventoryRow[] = [];
    products.forEach(product => {
      const variants = product.variants ?? [];
      const catName =
        product.category_details?.category_name ?? product.category_name ?? "";
      variants.forEach(variant => {
        rows.push({
          variantId: variant.id,
          productId: product.id,
          productName: product.product_name,
          categoryName: catName,
          image: product.image ?? product.image_url ?? null,
          value: variant.value ?? variant.weight ?? "Default",
          sku: variant.sku ?? "—",
          price: variant.price,
          totalStock: variant.total_stock ?? 0,
          reservedStock: variant.reserved_stock ?? 0,
          availableStock: variant.available_stock ?? 0,
          isActive: variant.is_active,
          inStock: variant.in_stock ?? true,
          vendor: "—", // Default vendor placeholder
          associatedVariants: variants,
          productDetails: product,
        });
      });
    });
    return rows;
  }, [products]);

  // Filter flattened rows client-side
  const filteredRows = useMemo(() => {
    return inventoryRows.filter(row => {
      if (!search.trim()) {
        return true;
      }
      const s = search.toLowerCase();
      return (
        row.productName.toLowerCase().includes(s) ||
        row.sku.toLowerCase().includes(s) ||
        row.value.toLowerCase().includes(s)
      );
    });
  }, [inventoryRows, search]);

  // Current variant details (if selected)
  const currentVariant = useMemo(() => {
    if (selectedVariantId === null) {
      return null;
    }
    return inventoryRows.find(r => r.variantId === selectedVariantId) ?? null;
  }, [inventoryRows, selectedVariantId]);

  // Compute pagination slices for the list view
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const totalPages = Math.ceil(filteredRows.length / pageSize);

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Adjust stock handler
  const handleAdjustStock = async () => {
    if (!currentVariant) {
      return;
    }
    const qty = parseInt(stockQuantity, 10);
    if (Number.isNaN(qty) || qty <= 0) {
      projectToast.error("Please enter a valid quantity greater than 0");
      return;
    }

    setSubmittingStock(true);
    try {
      await gruhUdhyogVariantService.adjustStock(currentVariant.variantId, {
        stock_action: stockAction,
        stock_quantity: qty,
      });
      projectToast.success(
        `Successfully ${stockAction === "ADD" ? "added" : "removed"} ${qty} items`
      );
      setIsAdjustOpen(false);
      setStockQuantity("0");
      // Reload products to fetch updated stock details
      await fetchProducts();
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Failed to adjust stock"));
    } finally {
      setSubmittingStock(false);
    }
  };

  return (
    <AdminShell title="Inventory" subtitle="Manage Your Inventory Items">
      {loading && products.length === 0 ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : selectedVariantId === null ? (
        // LIST VIEW (Image 1)
        <div className="space-y-6">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by Product name or SKU..."
              className="bg-muted/40 rounded-xl border-transparent pl-9"
            />
          </div>

          <div className="rounded-xl border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Product Title</TableHead>
                  <TableHead className="font-semibold">Variant Title</TableHead>
                  <TableHead className="font-semibold">SKU</TableHead>
                  <TableHead className="text-right font-semibold">
                    Total Stock
                  </TableHead>
                  <TableHead className="text-right font-semibold">
                    Available Stock
                  </TableHead>
                  <TableHead className="text-center font-semibold">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="h-32 text-center text-muted-foreground"
                    >
                      No inventory items found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map(row => (
                    <TableRow
                      key={row.variantId}
                      onClick={() => setSelectedVariantId(row.variantId)}
                      className="cursor-pointer hover:bg-gray-100"
                    >
                      <TableCell className="font-medium text-foreground">
                        {row.productName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.value}
                      </TableCell>

                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {row.sku}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {row.totalStock}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {row.availableStock}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setSelectedVariantId(row.variantId)}
                          className="h-8 w-8 rounded-lg hover:bg-muted"
                          title="View Variant Details"
                        >
                          <PencilIcon className="h-4 w-4 text-primary" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <TablePagination
            page={page}
            pageSize={pageSize}
            totalCount={filteredRows.length}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      ) : (
        // DETAIL VIEW (Image 2)
        <div className="space-y-6">
          {/* Top Bar with Back Button */}
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedVariantId(null)}
              className="flex items-center gap-1.5 rounded-lg border border-border"
            >
              <ChevronLeft className="h-4 w-4" /> Back to Inventory
            </Button>
          </div>

          {currentVariant ? (
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Left Column (Variant details & locations) */}
              <div className="space-y-6 lg:col-span-2">
                {/* Variant Details Card */}
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-foreground">
                      <span className="font-medium">
                        {currentVariant.value}
                      </span>{" "}
                      Details
                    </h2>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        SKU
                      </p>
                      <p className="mt-0.5 font-mono text-sm text-foreground">
                        {currentVariant.sku}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Total Stock
                      </p>
                      <p className="mt-0.5 text-sm font-medium text-foreground">
                        {currentVariant.totalStock}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Locations Card */}
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-base font-bold text-foreground">
                      Stock Details
                    </h2>
                  </div>

                  <div className="rounded-lg border border-border">
                    <Table>
                      <TableHeader className="bg-muted">
                        <TableRow>
                          <TableHead className="rounded-tl-lg font-semibold">
                            Name
                          </TableHead>
                          <TableHead className="text-center font-semibold">
                            Total Stock
                          </TableHead>
                          <TableHead className="text-center font-semibold">
                            Available Stock
                          </TableHead>
                          <TableHead className="rounded-tr-lg text-center font-semibold">
                            Action
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium text-foreground">
                            {currentVariant.value}
                          </TableCell>
                          <TableCell className="text-center">
                            {currentVariant.totalStock}
                          </TableCell>
                          <TableCell className="text-center">
                            {currentVariant.availableStock}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setStockAction("ADD");
                                setStockQuantity("0");
                                setIsAdjustOpen(true);
                              }}
                              className="h-8 w-8 rounded-lg hover:bg-muted"
                            >
                              <Pencil className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-3 text-right text-xs text-muted-foreground">
                    1-1 of 1 result
                  </div>
                </div>
              </div>

              {/* Right Column (Main Product and Associated Variants) */}
              <div className="space-y-6">
                {/* Main Product Card */}
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    Main Product
                  </h2>
                  <div className="flex items-start gap-3">
                    <div className="bg-muted/30 flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border">
                      {currentVariant.image ? (
                        <img
                          src={resolveMediaUrl(currentVariant.image) ?? ""}
                          alt={currentVariant.productName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Package className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate font-bold text-foreground">
                        {currentVariant.productName}
                      </h3>
                      <button
                        onClick={() => {
                          projectToast.info("Redirecting to product info...");
                        }}
                        className="mt-0.5 block text-xs text-muted-foreground hover:underline"
                      >
                        View Product Details
                      </button>
                    </div>
                  </div>
                </div>

                {/* Associated Variants Card */}
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    Associated Variants
                  </h2>
                  <div className="space-y-2">
                    {currentVariant.associatedVariants.map(v => {
                      const isActive = v.id === selectedVariantId;
                      return (
                        <button
                          key={v.id}
                          onClick={() => setSelectedVariantId(v.id)}
                          className={`hover:bg-muted/40 flex w-full items-start gap-3 rounded-lg border p-3 text-left transition ${isActive
                            ? "bg-primary/5 border-primary ring-1 ring-primary"
                            : "border-border bg-card"
                            }`}
                        >
                          <div className="bg-muted/40 flex h-8 w-8 shrink-0 items-center justify-center rounded">
                            <Boxes className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-foreground">
                              {v.value ?? v.weight ?? "Default"}
                            </div>
                            <div className="truncate font-mono text-[10px] text-muted-foreground">
                              SKU: {v.sku ?? "—"}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed py-12 text-center text-muted-foreground">
              Variant details not found.
            </div>
          )}
        </div>
      )}

      {/* Adjust Stock Modal (Image 3) */}
      <Dialog open={isAdjustOpen} onOpenChange={setIsAdjustOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              Adjust Stock (Add / Remove)
            </DialogTitle>
          </DialogHeader>

          {currentVariant && (
            <div className="space-y-5 py-2">
              <div>
                <span className="text-sm font-semibold text-muted-foreground">
                  Location :
                </span>{" "}
                <span className="text-sm font-bold text-foreground">
                  Padmavati Nagar, Indira Nagar, Vibhag 2, Lambha, Ahmedabad – 382405
                </span>
              </div>

              {/* Stock Summaries Display */}
              <div className="grid grid-cols-3 gap-2 rounded-lg border border-amber-900/10 bg-amber-900/5 p-3 text-center">
                <div>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">
                    Total Stock
                  </div>
                  <div className="text-base font-bold text-blue-600 text-foreground">
                    {currentVariant.totalStock}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">
                    Available Stock
                  </div>
                  <div className="text-base font-bold text-foreground text-green-600">
                    {currentVariant.availableStock}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">
                    Reserved (Orders)
                  </div>
                  <div className="text-base font-bold text-foreground text-yellow-600">
                    {currentVariant.reservedStock}
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="text-xs leading-relaxed text-muted-foreground">
                <span className="font-bold text-amber-900">Add (+):</span>{" "}
                Updates Total Stock (and Available).{" "}
                <span className="font-bold text-amber-900">Remove (-):</span>{" "}
                Only Available Stock decreases; Total Stock stays the same (e.g.
                store sale).
              </div>

              {/* Adjustment Type Selection */}
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Adjustment type
                </Label>
                <RadioGroup
                  value={stockAction}
                  onValueChange={(val: "ADD" | "REMOVE") => setStockAction(val)}
                  className="mt-1 flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ADD" id="add" />
                    <Label
                      htmlFor="add"
                      className="cursor-pointer text-sm font-medium"
                    >
                      Add stock (+)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="REMOVE" id="remove" />
                    <Label
                      htmlFor="remove"
                      className="cursor-pointer text-sm font-medium"
                    >
                      Remove stock (-)
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Quantity input */}
              <div className="space-y-2">
                <Label
                  htmlFor="qty"
                  className="text-xs font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Quantity to {stockAction === "ADD" ? "add" : "remove"} *
                </Label>
                <Input
                  id="qty"
                  type="number"
                  min="1"
                  value={stockQuantity}
                  onChange={e => setStockQuantity(e.target.value)}
                  className="rounded-lg border-border"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              disabled={submittingStock}
              onClick={() => setIsAdjustOpen(false)}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              disabled={submittingStock}
              onClick={handleAdjustStock}
              className="rounded-lg bg-amber-900/90 font-semibold text-white hover:bg-amber-900"
            >
              {submittingStock ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                "Apply"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
